import { CustomFilterWithGrid } from 'components/CustomTableFilter/CustomFilterWithGrid';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { AnyObject } from 'types/AnyObject';

import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { IAfterGuiAttachedParams } from 'ag-grid-community';
import type { CustomFilterProps } from 'ag-grid-react';
import { useGridFilter } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

type FilterProps = CustomFilterProps & {
    changeFilterHandler: (values: any) => void;
    dataAccessor: string;
    customRequestId?: string;
    query: string | TypedDocumentNode<any, any>;
    variables: Record<string, any>;
    whereVariables: string[];
    childFieldAccessor: string;
    columns: any[];
    groupAndSortItemsHelper?: (items: any[]) => any[];
    filterItems?: Record<string, any>[] | null;
    isClientSideSearch?: boolean;
    withSearch?: boolean;
    hideTableHeader?: boolean;
    customWhereVariablesGetter?: (searchStr: string) => any;
    additionalWhereVariables?: AnyObject | null;
};

export const AgGridCustomFilterAdapter = (props: FilterProps) => {
    const filterRef = useRef<() => void | null>();
    const {
        changeFilterHandler,
        groupAndSortItemsHelper,
        colDef: { field: fieldName },
        dataAccessor,
        customRequestId,
        query,
        variables,
        whereVariables,
        childFieldAccessor,
        columns,
        filterItems,
        isClientSideSearch,
        withSearch,
        customWhereVariablesGetter,
        additionalWhereVariables,
        onModelChange,
    } = props;

    const resetSearchRef = useRef<() => any>();

    const doesFilterPass = useCallback(() => true, []);
    const afterGuiAttached = useCallback(({ hidePopup }: IAfterGuiAttachedParams) => {
        filterRef.current = hidePopup;
        resetSearchRef?.current?.();
    }, []);

    useGridFilter({
        doesFilterPass,
        afterGuiAttached,
    });

    const requestId = useMemo(() => customRequestId || dataAccessor, [customRequestId, dataAccessor]);

    const [filterData, , , filterRequest] = useRequest({
        requestId,
        query,
        withEffect: false,
    });

    const onConfirmHandler = (value: any) => {
        const checkedValues = value.map((item: { name: string | null }) => item?.name);

        changeFilterHandler?.({ fieldName, checkedValues, fullDataSet: value });
        onCancelHandler();
        onModelChange?.({
            fieldName,
            checkedValues,
        });
    };
    const onCancelHandler = () => {
        filterRef?.current?.();
    };

    const items = useMemo(() => {
        if (groupAndSortItemsHelper) {
            return groupAndSortItemsHelper(filterData?.[dataAccessor]?.items);
        }

        return undefined;
    }, [dataAccessor, filterData, groupAndSortItemsHelper]);

    const actualItems = useMemo(
        () => filterItems || items || filterData?.[dataAccessor]?.items,
        [dataAccessor, filterData, filterItems, items],
    );

    const [resetFetchedAfterUnmount] = useResetStoreData({ requestId });

    useEffect(
        () => () => {
            resetFetchedAfterUnmount(undefined);
        },
        [resetFetchedAfterUnmount],
    );

    return (
        <CustomFilterWithGrid
            {...props}
            items={actualItems}
            isFetched={!!actualItems?.length}
            requestVariables={variables}
            requestFn={filterRequest}
            requestQuery={query}
            dataAccessor={dataAccessor}
            columnDefs={columns}
            childFieldAccessor={childFieldAccessor}
            whereVariables={whereVariables}
            onConfirm={onConfirmHandler}
            onCancel={onCancelHandler}
            filteredGroupHelper={groupAndSortItemsHelper}
            withSelectAllBtn
            isClientSideSearch={isClientSideSearch}
            customWhereVariablesGetter={customWhereVariablesGetter}
            withSearch={withSearch}
            fieldName={fieldName}
            additionalWhereVariables={additionalWhereVariables}
            resetSearchRef={resetSearchRef}
        />
    );
};

AgGridCustomFilterAdapter.displayName = 'AgGridCustomFilterAdapter';
